import { Component, computed, effect, inject, OnInit, signal, untracked } from '@angular/core';
import { AddressInputComponent } from '../address-input/address-input.component';
import { apply, disabled, form, FormField, hidden, max, min, required, schema, submit } from '@angular/forms/signals';
import { AddressSchemaService } from '@app/services/address-schema.service';
import { User } from '../user/user';
import { TranslatePipe } from '@ngx-translate/core';
import { BusinessProfileInputComponent } from '../business-profile-input/business-profile-input.component';
import { BusinessAttributes, createEmptyBusinessAttributes } from '../user/business-attributes';
import { AddressService, CountryService, NotificationService, PoeModalService } from '@app/services';
import { distinct } from '@app/logic/functions';
import { first, firstValueFrom, lastValueFrom } from 'rxjs';
import { ParametersService } from '../parameters/parameters.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserManagerService } from '@app/managers';
import { IonSpinner } from "@ionic/angular/standalone";
import { AddressValidationDto } from '@app/dtos/addresses/address-validation.dto';

@Component({
    selector: 'app-user-input',
    imports: [IonSpinner, AddressInputComponent, FormField, TranslatePipe, BusinessProfileInputComponent],
    templateUrl: './user-input.component.html',
    styleUrl: './user-input.component.scss'
})
export class UserInputComponent implements OnInit {
    private readonly addressSchemaService = inject(AddressSchemaService);
    private readonly countryService = inject(CountryService);
    private readonly parametersService = inject(ParametersService);
    private readonly addressService = inject(AddressService);
    private readonly poeModalService = inject(PoeModalService);
    private readonly userManagerService = inject(UserManagerService);
    private readonly notificationService = inject(NotificationService);
    addressSchema = this.addressSchemaService.addressSchema;
    countryResource = this.countryService.countries;
    countries = computed(() => {
        if (!this.countryResource.hasValue()) {
            return [];
        }
        return this.countryResource.value().filter(x => x.code !== 'IN' || x.name === 'India');
    });
    locales = this.countryService.locales;
    languages = computed(() =>
        this.locales.hasValue()
            ? distinct(this.locales.value().map(x => ({ code: x.languageCode, name: x.languageName })), x => x.code)
            : []
    );
    userModel = signal<User>(new User());
    params$ = this.parametersService.getParametersObservable().pipe(takeUntilDestroyed());
    private lastPushedLocaleCode: string | undefined;
    private lastPushedLanguageCode: string | undefined;

    ngOnInit(): void {
        this.params$.pipe(
            first(p => !!p.user)
        ).subscribe(params => this.userModel.set(structuredClone(params.user))); // deep copy

    }

    userForm = form(this.userModel, (schemaPath) => {
        apply(schemaPath.address, this.addressSchema);
        apply(schemaPath.businessAttributes, businessProfileSchema);
        required(schemaPath.businessType);
        required(schemaPath.firstName);
        required(schemaPath.lastName);
        required(schemaPath.languageCode);
        disabled(schemaPath.countryCode, () => !this.countryResource.hasValue());
        hidden(schemaPath.nameOfBusiness, ({ valueOf }) => valueOf(schemaPath.businessType) !== 'df');
        hidden(schemaPath.businessAttributes, ({ valueOf }) => !['cp', 'df'].includes(valueOf(schemaPath.businessType)));
    });

    private readonly _initializeBusinessAttributesEffect = effect(() => {
        const businessType = this.userForm.businessType().value();
        if (['cp', 'df'].includes(businessType)) {
            if(untracked(() => !this.userForm.businessAttributes().value())){
                const businessAttributes = createEmptyBusinessAttributes();
                if(businessType === 'cp') {
                    businessAttributes.name = `${this.userForm.firstName().value()} ${this.userForm.lastName().value()}`;
                }
                this.userForm.businessAttributes().value.set(businessAttributes);
            }
        } else {
            this.userForm.businessAttributes().value.set(null);
        }
    });
    private readonly _updateLanguageEffect = effect(() => {
        const selectedCountry = this.userForm.address().value().countryCode;
        const selectedLanguage = untracked(() => this.userForm.languageCode().value());
        const country = untracked(() => this.countries().find(c => c.code === selectedCountry));
        const browserLanguage = navigator.language.split('-')[0];
        const localeToSelect = country.locales.find(l => l.languageCode === selectedLanguage) ?? country.locales.find(l => l.languageCode === browserLanguage) ?? country.locales[0];
        untracked(() => this.userForm.languageCode()).value.set(localeToSelect.languageCode);
    });

    private readonly _setLocaleEffect = effect(() => {
        const selectedCountry = this.userForm.address().value().countryCode;
        const selectedLanguage = this.userForm.languageCode().value();
        const country = untracked(() => this.countries().find(c => c.code === selectedCountry));
        const browserLanguage = navigator.language.split('-')[0];
        const localeToSelect = country.locales.find(l => l.languageCode === selectedLanguage) ?? country.locales.find(l => l.languageCode === browserLanguage) ?? country.locales[0];
        untracked(() => this.userForm.language()).value.set(localeToSelect.languageName);
        untracked(() => this.userForm.locale()).value.set(localeToSelect.code);
    });

    private readonly _propagateLocaleToParametersEffect = effect(() => {
        const selectedCountry = this.userForm.address().value().countryCode;
        const selectedLanguage = this.userForm.languageCode().value();
        const country = untracked(() => this.countries().find(c => c.code === selectedCountry));
        if (!country?.locales?.length) {
            return;
        }
        const browserLanguage = navigator.language.split('-')[0];
        const localeToSelect = country.locales.find(l => l.languageCode === selectedLanguage) ?? country.locales.find(l => l.languageCode === browserLanguage) ?? country.locales[0];

        if (localeToSelect?.code && localeToSelect.code !== this.lastPushedLocaleCode) {
            this.lastPushedLocaleCode = localeToSelect.code;
            this.parametersService.setCountry(localeToSelect);
        }
        if (selectedLanguage && selectedLanguage !== this.lastPushedLanguageCode) {
            this.lastPushedLanguageCode = selectedLanguage;
            this.parametersService.setLanguage(selectedLanguage);
        }
    });

    protected async submitUser(event: Event) {
        event.preventDefault();
        await submit(this.userForm, async () => {
            const address: AddressValidationDto = structuredClone(this.userForm().value().address); // deep copy
            address.name = `${this.userForm().value().firstName} ${this.userForm().value().lastName}`;
            const addressValidationResult = await lastValueFrom(this.addressService.validate(address));
            if (addressValidationResult.validationStatus === 'INVALID') {
                return {
                    kind: 'validation_error',
                    message: 'AddressNotFound'
                };
            }
            if (addressValidationResult.validationStatus === 'SUSPICIOUS') {
                const selectedAddress = await firstValueFrom(this.poeModalService.showAddressSelectionModal(addressValidationResult.userInputAddress, addressValidationResult.suggestedAddress));
                if (selectedAddress === null) {
                    return;
                }
                if (selectedAddress === addressValidationResult.suggestedAddress) {
                    this.userForm.address().value.set(addressValidationResult.suggestedAddress);
                }
            }
            try {
                const user = await firstValueFrom(this.userManagerService.updateUser(this.userModel()))
                this.parametersService.setUser(user);
                this.notificationService.notifySavedSuccessfully();
            } catch {
                this.notificationService.notifyErrorWithTranslate('Error', 'CheckInputAndTryAgainLater');
            }
        });
    }
}

const businessProfileSchema = schema<BusinessAttributes>((path) => {
    min(path.businessArea, 0);
    min(path.areaOfArableLand, 0);
    max(path.areaOfArableLand, 100);

    min(path.countOfDairyCows, 0);
    min(path.countOfBeef, 0);
    min(path.countOfPig, 0);
    min(path.countOfFowl, 0);
    min(path.countOfOtherAnimals, 0);

    disabled(path.countOfDairyCows, ({ valueOf }) => valueOf(path.hasDairyCows) !== true);
    disabled(path.countOfBeef, ({ valueOf }) => valueOf(path.hasBeef) !== true);
    disabled(path.countOfPig, ({ valueOf }) => valueOf(path.hasPig) !== true);
    disabled(path.countOfFowl, ({ valueOf }) => valueOf(path.hasFowl) !== true);
    disabled(path.countOfOtherAnimals, ({ valueOf }) => valueOf(path.hasOtherAnimals) !== true);
});